import * as React from "react";

export type SelectionMode = "all" | "currentPage" | "specific";

const POPOVER_OFFSET_TOP = 4;
export const RADIX_10 = 10;

/**
 * Shared popover behaviour for selection popovers (header-level and group-level).
 * Handles open/close state, portal positioning, click-outside/Escape dismissal,
 * specific quantity input state, and validation.
 */
export const useSelectionPopover = (maxLimit: number) => {
  const [open, setOpen] = React.useState(false);
  const [selectionMode, setSelectionMode] = React.useState<SelectionMode>("all");
  const [specificQuantity, setSpecificQuantity] = React.useState("");
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);

  const updatePosition = React.useCallback(() => {
    if (!triggerRef.current || !panelRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    panelRef.current.style.top = `${rect.bottom + POPOVER_OFFSET_TOP}px`;
    panelRef.current.style.left = `${rect.left}px`;
  }, []);

  React.useLayoutEffect(() => {
    if (!open) return;
    updatePosition();
  }, [open, updatePosition]);

  React.useEffect(() => {
    if (!open) return;
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [open, updatePosition]);

  React.useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target;
      if (!(target instanceof Node)) return;
      if (triggerRef.current && !triggerRef.current.contains(target) && panelRef.current && !panelRef.current.contains(target)) {
        setOpen(false);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- resets quantity input when popover closes
      setSpecificQuantity("");
    }
  }, [open]);

  const parsedQty = Number.parseInt(specificQuantity, RADIX_10);
  const isOverMax = !Number.isNaN(parsedQty) && parsedQty > maxLimit;
  const isApplyDisabled = !specificQuantity || Number.isNaN(parsedQty) || parsedQty <= 0 || isOverMax;

  return {
    open,
    setOpen,
    selectionMode,
    setSelectionMode,
    specificQuantity,
    setSpecificQuantity,
    triggerRef,
    panelRef,
    isOverMax,
    isApplyDisabled,
  };
};
